import React, { useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import tareoService from '../services/tareoService';
import ExcelJS from 'exceljs';
import { hasPermission, PERMISSIONS } from '../utils/permissions';

const TIPOS_EVENTO = [
  'asistio', 'falta', 'feriado', 'feriado_laborado', 'descanso', 'descanso_laborado',
  'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad', 'permiso', 'vacaciones'
];

// Eventos en los que el sistema conserva el teórico pero las horas reales deben ser 0 (vacías en Excel).
const TIPOS_EVENTO_HORAS_REALES_NULAS = [
  'falta', 'feriado', 'descanso', 'permiso', 'vacaciones',
  'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad'
];
// Feriado laborado / descanso laborado: el sistema los trata como asistió puntual (real = teórico si no se indica).
const TIPOS_EVENTO_REAL_IGUAL_TEORICO = ['feriado_laborado', 'descanso_laborado'];

const normalizarFecha = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor.trim())) return valor.trim();
  const num = Number(valor);
  if (!isNaN(num) && num > 0) {
    const date = new Date((num - 25569) * 86400 * 1000);
    const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const str = String(valor).trim();
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return null;
};

const normalizarHora = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const s = String(valor).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':').map(Number);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  if (/^\d{4}$/.test(s)) return `${s.slice(0, 2)}:${s.slice(2)}`;
  return null;
};

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const ImportadorTareos = ({ onClose, onImportSuccess, selectedCompany, selectedProject, employees = [], plameConceptos = [] }) => {
  const now = new Date();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-11
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [archivo, setArchivo] = useState(null);
  const [procesandoArchivo, setProcesandoArchivo] = useState(false);
  const [datosExcel, setDatosExcel] = useState([]);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [filasValidas, setFilasValidas] = useState([]);
  const [mostrarValidaciones, setMostrarValidaciones] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const puedeAprobarPermisos = hasPermission(user, PERMISSIONS.APPROVE_PAYROLLS);

  // Modo: 'importar' (crear tareos) o 'regularizar' (solo añadir conceptos a tareos existentes)
  const [modoImportacion, setModoImportacion] = useState('importar');
  const fileInputRefRegularizar = useRef(null);
  const [archivoRegularizar, setArchivoRegularizar] = useState(null);
  const [procesandoRegularizar, setProcesandoRegularizar] = useState(false);
  const [datosRegularizar, setDatosRegularizar] = useState([]);
  const [erroresRegularizar, setErroresRegularizar] = useState([]);
  const [filasValidasRegularizar, setFilasValidasRegularizar] = useState([]);
  const [mostrarValidacionesRegularizar, setMostrarValidacionesRegularizar] = useState(false);
  const [aplicandoRegularizar, setAplicandoRegularizar] = useState(false);
  const [resultadoRegularizar, setResultadoRegularizar] = useState(null);

  // Empleados del proyecto que estuvieron activos en el mes/año seleccionado (según historial laboral)
  const empleadosImportables = React.useMemo(() => {
    if (!selectedProject || !employees.length) return [];
    const firstDay = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const lastDayNum = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const lastDay = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(lastDayNum).padStart(2, '0')}`;
    const delProyecto = employees.filter(emp => {
      const proy = (emp.proyecto || '').toString().trim();
      const proyId = emp.proyecto_id != null ? String(emp.proyecto_id) : '';
      return proy === (selectedProject.nombre || '').trim() || proyId === String(selectedProject.id);
    });
    return delProyecto.filter(emp => {
      const h = emp.ultimo_historial;
      if (!h || !h.fecha_ingreso) return false;
      const ingreso = String(h.fecha_ingreso).split('T')[0];
      if (ingreso > lastDay) return false;
      if (!h.fecha_cese) return true;
      const cese = String(h.fecha_cese).split('T')[0];
      return cese >= firstDay;
    });
  }, [employees, selectedProject, selectedMonth, selectedYear]);

  const empleadoPorDni = (dni) => {
    const d = String(dni ?? '').trim();
    return employees.find(e => String(e.numero_documento ?? '').trim() === d);
  };

  const empleadoActivoEnFecha = (empleado, fechaStr) => {
    if (!empleado?.ultimo_historial) return false;
    const h = empleado.ultimo_historial;
    const f = fechaStr;
    if (!h.fecha_ingreso) return false;
    const ingreso = String(h.fecha_ingreso).split('T')[0];
    if (f < ingreso) return false;
    if (!h.fecha_cese) return true;
    const cese = String(h.fecha_cese).split('T')[0];
    return f <= cese;
  };

  // Alinear tipo con PLAME: clasificación 0700 (códigos 07xx) son descuentos, no "otro"
  const tipoConceptoParaTareo = (c) => {
    const cod = String(c.codigo_plame || c.codigo || '').trim();
    const tipo = c.tipo_concepto;
    if (tipo === 'otro' && /^07\d{2}$/.test(cod)) return 'descuento';
    return tipo;
  };

  // Resolver concepto por código PLAME o por nombre (id)
  const resolverConcepto = (valor) => {
    if (valor === null || valor === undefined || String(valor).trim() === '') return null;
    const v = String(valor).trim();
    const porCodigo = plameConceptos.find(c => String(c.codigo || '').trim() === v);
    if (porCodigo) return { id: porCodigo.id, tipo_concepto: tipoConceptoParaTareo(porCodigo) };
    const porNombre = plameConceptos.find(c => (c.nombre || '').toLowerCase() === v.toLowerCase());
    if (porNombre) return { id: porNombre.id, tipo_concepto: tipoConceptoParaTareo(porNombre) };
    const porId = plameConceptos.find(c => String(c.id) === v);
    if (porId) return { id: porId.id, tipo_concepto: tipoConceptoParaTareo(porId) };
    return null;
  };

  const procesarArchivo = async (file) => {
    setProcesandoArchivo(true);
    setDatosExcel([]);
    setErroresValidacion([]);
    setFilasValidas([]);
    setMostrarValidaciones(false);
    setResultado(null);
    try {
      const buffer = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      const ws = wb.worksheets.find(s => s.name.toLowerCase() === 'tareos') || wb.worksheets[0];
      if (!ws) {
        setErroresValidacion([{ fila: 0, nombre: '-', mensaje: 'No se encontró la hoja "Tareos" en el archivo.' }]);
        setMostrarValidaciones(true);
        setProcesandoArchivo(false);
        return;
      }
      const headerRow = ws.getRow(1);
      const headers = [];
      headerRow.eachCell((cell, colNumber) => { headers[colNumber - 1] = String(cell.value ?? '').trim(); });
      const getVal = (obj, keys) => {
        for (const k of keys) if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
        return '';
      };
      const rows = [];
      for (let rowNumber = 2; rowNumber <= ws.rowCount; rowNumber++) {
        const row = ws.getRow(rowNumber);
        const obj = {};
        row.eachCell((cell, colNumber) => { obj[headers[colNumber - 1]] = cell.value; });
        const dni = getVal(obj, ['DNI', 'dni', 'Número Documento']);
        if (dni === '' && Object.values(obj).every(v => v === undefined || v === null || v === '')) continue;
        rows.push({
          dni,
          fecha: getVal(obj, ['Fecha', 'fecha']),
          tipo_evento: getVal(obj, ['Tipo evento', 'tipo_evento', 'Tipo']),
          hora_entrada_teorica: getVal(obj, ['Hora entrada teórica', 'hora_entrada_teorica', 'Hora entrada']) || '08:00',
          hora_salida_teorica: getVal(obj, ['Hora salida teórica', 'hora_salida_teorica', 'Hora salida']) || '17:00',
          hora_entrada_real: getVal(obj, ['Hora entrada real', 'hora_entrada_real']),
          hora_salida_real: getVal(obj, ['Hora salida real', 'hora_salida_real']),
          observaciones: getVal(obj, ['Observaciones', 'observaciones']),
          concepto: getVal(obj, ['Concepto', 'concepto']),
          monto_concepto: getVal(obj, ['Monto concepto', 'monto_concepto'])
        });
      }
      const limpio = rows.map(r => ({
        dni: String(r.dni ?? '').trim(),
        fecha: r.fecha,
        tipo_evento: String(r.tipo_evento ?? '').trim().toLowerCase().replace(/\s+/g, '_'),
        hora_entrada_teorica: r.hora_entrada_teorica ?? '08:00',
        hora_salida_teorica: r.hora_salida_teorica ?? '17:00',
        hora_entrada_real: r.hora_entrada_real ?? '',
        hora_salida_real: r.hora_salida_real ?? '',
        observaciones: String(r.observaciones ?? '').trim(),
        concepto: String(r.concepto ?? '').trim(),
        monto_concepto: r.monto_concepto
      }));
      setDatosExcel(limpio);
      validarDatos(limpio);
    } catch (err) {
      setErroresValidacion([{ fila: 0, nombre: '-', mensaje: 'Error al leer el Excel: ' + (err.message || 'archivo inválido') }]);
      setMostrarValidaciones(true);
    }
    setProcesandoArchivo(false);
  };

  const validarDatos = (filas) => {
    const errores = [];
    const validas = [];
    filas.forEach((fila, idx) => {
      const nFila = idx + 2;
      const errFila = [];
      const dni = String(fila.dni ?? '').trim();
      if (!dni) errFila.push('DNI es obligatorio');
      const emp = empleadoPorDni(dni);
      if (dni && !emp) errFila.push('No se encontró empleado con este DNI en el proyecto/empresa');
      const fecha = normalizarFecha(fila.fecha);
      if (!fecha) errFila.push('Fecha inválida o vacía (use YYYY-MM-DD o formato de fecha válido)');
      if (emp && fecha && !empleadoActivoEnFecha(emp, fecha)) errFila.push('El empleado no estaba activo en esta fecha según su historial laboral');
      const tipo = fila.tipo_evento;
      if (!tipo) errFila.push('Tipo de evento es obligatorio');
      if (tipo && !TIPOS_EVENTO.includes(tipo)) errFila.push('Tipo de evento no válido. Use: ' + TIPOS_EVENTO.join(', '));
      const he = normalizarHora(fila.hora_entrada_teorica) || '08:00';
      const hs = normalizarHora(fila.hora_salida_teorica) || '17:00';
      if (!he) errFila.push('Hora entrada teórica inválida');
      if (!hs) errFila.push('Hora salida teórica inválida');
      // Para falta, feriado, descanso, permiso, vacaciones, descanso_medico, reposo_medico, subsidio: horas reales deben estar vacías
      const heReal = normalizarHora(fila.hora_entrada_real);
      const hsReal = normalizarHora(fila.hora_salida_real);
      if (tipo && TIPOS_EVENTO_HORAS_REALES_NULAS.includes(tipo) && (heReal || hsReal)) {
        errFila.push(`Para el tipo de evento "${tipo}" las horas reales deben estar vacías (el sistema conserva el teórico y registra real en 0).`);
      }
      // Concepto opcional: si se indica concepto, debe existir y monto > 0
      let conceptosPayload = [];
      if (fila.concepto) {
        const res = resolverConcepto(fila.concepto);
        if (!res) errFila.push('Concepto no reconocido (use código PLAME o nombre del concepto)');
        const monto = parseFloat(fila.monto_concepto);
        if (isNaN(monto) || monto <= 0) errFila.push('Monto concepto debe ser un número mayor a 0');
        if (res && !isNaN(monto) && monto > 0) conceptosPayload = [{ plame_configuracion_id: res.id, tipo_concepto: res.tipo_concepto, monto, observaciones: null }];
      } else if (fila.monto_concepto && parseFloat(fila.monto_concepto) > 0) {
        errFila.push('Si indica monto concepto debe indicar también el concepto');
      }
      if (errFila.length) {
        errores.push({ fila: nFila, nombre: (emp?.nombre_completo ?? dni) || '-', mensaje: errFila.join('; ') });
      } else {
        let horaEntradaReal = normalizarHora(fila.hora_entrada_real) || null;
        let horaSalidaReal = normalizarHora(fila.hora_salida_real) || null;
        if (TIPOS_EVENTO_HORAS_REALES_NULAS.includes(tipo)) {
          horaEntradaReal = null;
          horaSalidaReal = null;
        } else if (TIPOS_EVENTO_REAL_IGUAL_TEORICO.includes(tipo)) {
          // Feriado laborado / descanso laborado: siempre tomar teóricos como reales (como asistió puntual)
          horaEntradaReal = he;
          horaSalidaReal = hs;
        }
        validas.push({
          fila: nFila,
          empresa_id: selectedCompany.id,
          empleado_id: emp.id,
          proyecto_id: selectedProject.id,
          fecha,
          tipo_evento: tipo,
          hora_entrada_teorica: he,
          hora_salida_teorica: hs,
          hora_entrada_real: horaEntradaReal,
          hora_salida_real: horaSalidaReal,
          observaciones: String(fila.observaciones ?? '').trim() || null,
          conceptos: conceptosPayload.length ? conceptosPayload : undefined
        });
      }
    });
    setErroresValidacion(errores);
    setFilasValidas(validas);
    setMostrarValidaciones(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setArchivo(file);
      procesarArchivo(file);
    }
  };

  const descargarPlantilla = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Planillas - Importador Tareos';

    // Hoja Leyenda
    const wsLeyenda = wb.addWorksheet('Leyenda', { views: [{ state: 'frozen', ySplit: 0 }] });
    const leyendaRows = [
      ['LEYENDA - IMPORTACIÓN DE TAREOS'],
      [],
      ['Columna', 'Descripción', 'Valores / Formato'],
      ['DNI', 'Documento del empleado (obligatorio)', '8 dígitos, debe existir en la empresa'],
      ['Fecha', 'Fecha del tareo', 'YYYY-MM-DD (ej: 2025-01-15)'],
      ['Tipo evento', 'Tipo de registro (obligatorio). Use exactamente el valor de la columna "Valor en Excel"', 'Ver lista completa abajo'],
      ['Hora entrada teórica', 'Hora de entrada programada', 'HH:MM (ej: 08:00)'],
      ['Hora salida teórica', 'Hora de salida programada', 'HH:MM (ej: 17:00)'],
      ['Hora entrada real', 'Hora real de entrada (opcional)', 'HH:MM o vacío'],
      ['Hora salida real', 'Hora real de salida (opcional)', 'HH:MM o vacío'],
      ['Observaciones', 'Comentarios (opcional)', 'Texto libre'],
      ['Concepto', 'Código PLAME o nombre del concepto (opcional)', 'Ej: 0601, TARDANZAS, ADELANTO'],
      ['Monto concepto', 'Monto del concepto si aplica (opcional)', 'Número mayor a 0. Si pone monto, debe indicar Concepto'],
      [],
      ['TIPOS DE EVENTO VÁLIDOS (escriba en la columna "Tipo evento" el Valor en Excel):'],
      ['Valor en Excel', 'Nombre'],
      ['asistio', 'Asistió'],
      ['falta', 'Falta'],
      ['feriado', 'Feriado'],
      ['feriado_laborado', 'Feriado Laborado'],
      ['descanso', 'Descanso'],
      ['descanso_laborado', 'Descanso Laborado'],
      ['descanso_medico', 'Descanso Médico'],
      ['reposo_medico', 'Reposo Médico'],
      ['subsidio_por_enfermedad', 'Subsidio por Enfermedad'],
      ['permiso', 'Permiso'],
      ['vacaciones', 'Vacaciones'],
      [],
      ['HORAS REALES POR TIPO DE EVENTO:'],
      ['Tipo evento', 'Regla', 'En Excel: Hora entrada real / Hora salida real'],
      ['falta, feriado, descanso, permiso, vacaciones, descanso_medico, reposo_medico, subsidio_por_enfermedad', 'El sistema conserva el teórico pero registra horas reales en 0.', 'DEJAR VACÍO (obligatorio).'],
      ['feriado_laborado, descanso_laborado', 'Se tratan como asistió puntual: el sistema siempre usa los horarios teóricos como reales.', 'Deje vacío o igual al teórico; se guardarán como teórico.'],
      ['asistio', 'Se usan las horas reales que indique.', 'Opcional; si vacío depende de la configuración del proyecto.'],
      [],
      ['Nota: El empleado debe estar activo en la fecha indicada según su historial laboral (fecha de ingreso/cese).']
    ];
    leyendaRows.forEach((row, i) => {
      const r = wsLeyenda.getRow(i + 1);
      r.values = [undefined, ...row];
      if (i === 0) r.font = { bold: true, size: 14 };
      if (i === 2 || i === 12 || i === 13 || i === 15 || i === 16) r.font = { bold: true };
    });
    wsLeyenda.getColumn(1).width = 22;
    wsLeyenda.getColumn(2).width = 50;
    wsLeyenda.getColumn(3).width = 60;

    // Hoja Tareos (datos)
    const wsTareos = wb.addWorksheet('Tareos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headers = ['DNI', 'Fecha', 'Tipo evento', 'Hora entrada teórica', 'Hora salida teórica', 'Hora entrada real', 'Hora salida real', 'Observaciones', 'Concepto', 'Monto concepto'];
    const headerRow = wsTareos.getRow(1);
    headerRow.values = [undefined, ...headers];
    headerRow.font = { bold: true };
    const datosEjemplo = [
      ['12345678', '2025-01-15', 'asistio', '08:00', '17:00', '08:00', '17:00', '', '', ''],
      ['87654321', '2025-01-15', 'falta', '08:00', '17:00', '', '', 'Justificado', '', ''],
      ['12345678', '2025-01-16', 'asistio', '08:00', '17:00', '08:15', '17:00', '', 'TARDANZAS', '50']
    ];
    datosEjemplo.forEach((fila, i) => {
      const r = wsTareos.getRow(i + 2);
      r.values = [undefined, ...fila];
    });
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach(c => { wsTareos.getColumn(c).width = Math.max(18, (headers[c - 1] || '').length + 2); });

    // Hoja Empleados permitidos (según mes/año y proyecto seleccionado)
    const wsEmpleados = wb.addWorksheet('Empleados permitidos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const mesNombre = MESES[selectedMonth] || '';
    wsEmpleados.getRow(1).values = [undefined, `Empleados que puede incluir en el tareo - ${selectedProject?.nombre || 'Proyecto'} - ${mesNombre} ${selectedYear}`];
    wsEmpleados.getRow(1).font = { bold: true, size: 12 };
    wsEmpleados.getRow(2).values = [undefined, 'DNI', 'Nombre completo', 'Cargo'];
    wsEmpleados.getRow(2).font = { bold: true };
    empleadosImportables.forEach((emp, i) => {
      const r = wsEmpleados.getRow(i + 3);
      r.values = [undefined, emp.numero_documento || '', emp.nombre_completo || '', emp.cargo || ''];
    });
    wsEmpleados.getColumn(1).width = 14;
    wsEmpleados.getColumn(2).width = 35;
    wsEmpleados.getColumn(3).width = 25;

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_importacion_tareos.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const descargarPlantillaRegularizar = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Planillas - Regularizar conceptos';

    const wsLeyenda = wb.addWorksheet('Leyenda', { views: [{ state: 'frozen', ySplit: 0 }] });
    const leyendaRows = [
      ['LEYENDA - REGULARIZAR CONCEPTOS EN TAREOS EXISTENTES'],
      [],
      ['Este archivo sirve para AÑADIR conceptos (adelanto, ingreso o descuento) a tareos ya creados.'],
      ['No crea tareos nuevos. Solo busca el tareo por DNI + Fecha + Proyecto y le agrega el concepto indicado.'],
      [],
      ['Columna', 'Descripción', 'Valores / Formato'],
      ['DNI', 'Documento del empleado (obligatorio)', 'Debe existir en el proyecto y tener un tareo en la fecha indicada'],
      ['Fecha', 'Fecha del tareo existente', 'YYYY-MM-DD (ej: 2025-01-15)'],
      ['Concepto', 'Código PLAME o nombre del concepto (obligatorio)', 'Use código o nombre de la lista "Conceptos disponibles" más abajo'],
      ['Monto concepto', 'Monto del concepto (obligatorio)', 'Número mayor a 0'],
      ['Observaciones', 'Comentarios opcionales', 'Texto libre'],
      [],
      ['Personal al que aplica:'],
      ['Solo puede regularizar conceptos para empleados del proyecto seleccionado que estén activos en el mes/año elegido.'],
      ['La hoja "Empleados permitidos" contiene la lista de DNI y nombres que puede usar.'],
      ['El tareo debe existir previamente (mismo empleado, misma fecha, mismo proyecto).'],
      [],
      ['CONCEPTOS DISPONIBLES (use Código o Nombre en la columna Concepto):'],
    ];
    leyendaRows.forEach((row, i) => {
      const r = wsLeyenda.getRow(i + 1);
      r.values = [undefined, ...(Array.isArray(row) ? row : [row])];
      if (i === 0) r.font = { bold: true, size: 14 };
      if (i === 5) r.font = { bold: true };
      if (i === leyendaRows.length - 1) r.font = { bold: true };
    });
    let rowLeyenda = leyendaRows.length + 1;
    wsLeyenda.getRow(rowLeyenda).values = [undefined, 'Código', 'Nombre', 'Tipo'];
    wsLeyenda.getRow(rowLeyenda).font = { bold: true };
    rowLeyenda += 1;
    (plameConceptos || []).forEach((c) => {
      const r = wsLeyenda.getRow(rowLeyenda);
      r.values = [undefined, c.codigo_plame || c.codigo || '', c.nombre || '', tipoConceptoParaTareo(c) || ''];
      rowLeyenda += 1;
    });
    wsLeyenda.getColumn(1).width = 22;
    wsLeyenda.getColumn(2).width = 55;
    wsLeyenda.getColumn(3).width = 60;

    const wsReg = wb.addWorksheet('Regularizar conceptos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headersReg = ['DNI', 'Fecha', 'Concepto', 'Monto concepto', 'Observaciones'];
    wsReg.getRow(1).values = [undefined, ...headersReg];
    wsReg.getRow(1).font = { bold: true };
    const ejemploReg = [
      ['12345678', '2025-01-15', 'TARDANZAS', '50', ''],
      ['87654321', '2025-01-16', '0601', '100', 'Adelanto'],
    ];
    ejemploReg.forEach((fila, i) => {
      const r = wsReg.getRow(i + 2);
      r.values = [undefined, ...fila];
    });
    [1, 2, 3, 4, 5].forEach(c => { wsReg.getColumn(c).width = Math.max(16, (headersReg[c - 1] || '').length + 2); });

    const wsEmpleadosReg = wb.addWorksheet('Empleados permitidos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const mesNombre = MESES[selectedMonth] || '';
    wsEmpleadosReg.getRow(1).values = [undefined, `Personal al que aplica - ${selectedProject?.nombre || 'Proyecto'} - ${mesNombre} ${selectedYear}`];
    wsEmpleadosReg.getRow(1).font = { bold: true, size: 12 };
    wsEmpleadosReg.getRow(2).values = [undefined, 'DNI', 'Nombre completo', 'Cargo'];
    wsEmpleadosReg.getRow(2).font = { bold: true };
    empleadosImportables.forEach((emp, i) => {
      const r = wsEmpleadosReg.getRow(i + 3);
      r.values = [undefined, emp.numero_documento || '', emp.nombre_completo || '', emp.cargo || ''];
    });
    wsEmpleadosReg.getColumn(1).width = 14;
    wsEmpleadosReg.getColumn(2).width = 35;
    wsEmpleadosReg.getColumn(3).width = 25;

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_regularizar_conceptos.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const procesarArchivoRegularizar = async (file) => {
    setProcesandoRegularizar(true);
    setDatosRegularizar([]);
    setErroresRegularizar([]);
    setFilasValidasRegularizar([]);
    setMostrarValidacionesRegularizar(false);
    setResultadoRegularizar(null);
    const getVal = (obj, keys) => {
      for (const k of keys) if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
      return '';
    };
    try {
      const buffer = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      const ws = wb.worksheets.find(s => (s.name || '').toLowerCase().includes('regularizar')) || wb.worksheets[0];
      if (!ws) {
        setErroresRegularizar([{ fila: 0, mensaje: 'No se encontró la hoja "Regularizar conceptos" en el archivo.' }]);
        setMostrarValidacionesRegularizar(true);
        setProcesandoRegularizar(false);
        return;
      }
      const headerRow = ws.getRow(1);
      const headers = [];
      headerRow.eachCell((cell, colNumber) => { headers[colNumber - 1] = String(cell.value ?? '').trim(); });
      const rows = [];
      for (let rowNumber = 2; rowNumber <= (ws.rowCount || 2); rowNumber++) {
        const row = ws.getRow(rowNumber);
        const obj = {};
        row.eachCell((cell, colNumber) => { obj[headers[colNumber - 1]] = cell.value; });
        const dni = getVal(obj, ['DNI', 'dni']);
        const concepto = getVal(obj, ['Concepto', 'concepto']);
        const monto = getVal(obj, ['Monto concepto', 'monto_concepto', 'Monto']);
        if (dni === '' && concepto === '' && (monto === '' || monto === undefined)) continue;
        rows.push({
          dni: String(dni).trim(),
          fecha: getVal(obj, ['Fecha', 'fecha']),
          concepto: String(concepto).trim(),
          monto_concepto: monto,
          observaciones: String(getVal(obj, ['Observaciones', 'observaciones'])).trim()
        });
      }
      setDatosRegularizar(rows);
      validarDatosRegularizar(rows);
    } catch (err) {
      setErroresRegularizar([{ fila: 0, mensaje: 'Error al leer el Excel: ' + (err.message || 'archivo inválido') }]);
      setMostrarValidacionesRegularizar(true);
    }
    setProcesandoRegularizar(false);
  };

  const validarDatosRegularizar = (filas) => {
    const errores = [];
    const validas = [];
    filas.forEach((fila, idx) => {
      const nFila = idx + 2;
      const errFila = [];
      const dni = String(fila.dni ?? '').trim();
      if (!dni) errFila.push('DNI es obligatorio');
      const emp = empleadoPorDni(dni);
      if (dni && !emp) errFila.push('No se encontró empleado con este DNI en el proyecto/empresa');
      const fecha = normalizarFecha(fila.fecha);
      if (!fecha) errFila.push('Fecha inválida o vacía (use YYYY-MM-DD)');
      const conceptoVal = String(fila.concepto ?? '').trim();
      if (!conceptoVal) errFila.push('Concepto es obligatorio');
      const res = resolverConcepto(conceptoVal || fila.concepto);
      if (conceptoVal && !res) errFila.push('Concepto no reconocido (use código PLAME o nombre del concepto)');
      const monto = parseFloat(fila.monto_concepto);
      if (isNaN(monto) || monto <= 0) errFila.push('Monto concepto debe ser un número mayor a 0');
      if (errFila.length) {
        errores.push({ fila: nFila, nombre: (emp?.nombre_completo ?? dni) || '-', mensaje: errFila.join('; ') });
      } else {
        const conceptosPayload = [{ plame_configuracion_id: res.id, tipo_concepto: res.tipo_concepto, monto, observaciones: (fila.observaciones || null) }];
        validas.push({
          fila: nFila,
          empresa_id: selectedCompany.id,
          empleado_id: emp.id,
          proyecto_id: selectedProject.id,
          fecha,
          conceptos: conceptosPayload
        });
      }
    });
    setErroresRegularizar(errores);
    setFilasValidasRegularizar(validas);
    setMostrarValidacionesRegularizar(true);
  };

  const ejecutarRegularizacion = async () => {
    if (filasValidasRegularizar.length === 0) return;
    setAplicandoRegularizar(true);
    setResultadoRegularizar(null);
    try {
      const res = await tareoService.regularizarConceptos(filasValidasRegularizar);
      if (res.success) {
        setResultadoRegularizar({ ok: true, data: res.data, message: res.message });
        if (onImportSuccess) onImportSuccess(res);
      } else {
        setResultadoRegularizar({ ok: false, message: res.message || 'Error al regularizar', data: res.data });
      }
    } catch (err) {
      const data = err.response?.data;
      setResultadoRegularizar({ ok: false, message: data?.message || err.message || 'Error al regularizar' });
    }
    setAplicandoRegularizar(false);
  };

  const handleFileSelectRegularizar = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setArchivoRegularizar(file);
      procesarArchivoRegularizar(file);
    }
  };

  const ejecutarImportacion = async () => {
    if (filasValidas.length === 0) return;
    setImportando(true);
    setResultado(null);
    try {
      const payload = {
        tareos: filasValidas,
        crear_autorizaciones_aprobadas: puedeAprobarPermisos
      };
      const res = await tareoService.crearTareoMasivo(payload);
      if (res.success) {
        setResultado({ ok: true, data: res.data, message: res.message });
        if (onImportSuccess) onImportSuccess(res);
      } else {
        const erroresActividad = res.errors?.errores_actividad || res.errores_actividad;
        const erroresTipoEvento = res.errors?.errores_tipo_evento || res.errores_tipo_evento;
        const msg = res.message || 'Error al importar';
        setResultado({
          ok: false,
          message: msg,
          errores_actividad: erroresActividad,
          errores_tipo_evento: erroresTipoEvento
        });
      }
    } catch (err) {
      const data = err.response?.data;
      const erroresActividad = data?.errores_actividad;
      const erroresTipoEvento = data?.errores_tipo_evento;
      setResultado({
        ok: false,
        message: data?.message || err.message || 'Error al importar',
        errores_actividad: erroresActividad,
        errores_tipo_evento: erroresTipoEvento
      });
    }
    setImportando(false);
  };

  const hayErroresActividad = resultado?.errores_actividad?.length > 0;
  const hayErroresTipoEvento = resultado?.errores_tipo_evento?.length > 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full my-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Importar Tareos desde Excel</h2>
              <p className="text-sm text-gray-600 mt-1">
                Proyecto: <strong>{selectedProject?.nombre}</strong>. Los empleados deben estar activos en la fecha del tareo (historial laboral).
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="flex border-b border-gray-200 mb-4">
            <button
              type="button"
              onClick={() => {
                setModoImportacion('importar');
                setResultado(null);
                setResultadoRegularizar(null);
                setArchivoRegularizar(null);
                setMostrarValidacionesRegularizar(false);
                setDatosRegularizar([]);
                setErroresRegularizar([]);
                setFilasValidasRegularizar([]);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${modoImportacion === 'importar' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Importar tareos
            </button>
            <button
              type="button"
              onClick={() => {
                setModoImportacion('regularizar');
                setResultado(null);
                setResultadoRegularizar(null);
                setArchivo(null);
                setMostrarValidaciones(false);
                setDatosExcel([]);
                setErroresValidacion([]);
                setFilasValidas([]);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${modoImportacion === 'regularizar' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Regularizar conceptos
            </button>
          </div>

          {modoImportacion === 'regularizar' && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Regularizar conceptos:</strong> Añade conceptos (adelanto, ingreso o descuento) a <strong>tareos ya existentes</strong>. No crea tareos nuevos. El empleado debe tener un registro de tareo en la fecha indicada para el mismo proyecto. Descargue la plantilla con leyenda y la lista de personal al que aplica.
              </p>
            </div>
          )}

          {modoImportacion === 'importar' && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Importante:</strong> El sistema validará que cada empleado haya estado activo en la fecha indicada según su historial laboral. La plantilla incluye una hoja <strong>Leyenda</strong> con la descripción de cada columna. Puede agregar un <strong>Concepto</strong> y su <strong>Monto</strong> por fila (opcional).
              </p>
            </div>
          )}

          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Mes y año del tareo</h3>
            <p className="text-xs text-gray-500 mb-3">
              Elija el mes y año para ver los empleados que puede incluir según el proyecto y su historial laboral. La plantilla descargada incluirá esta lista en la hoja &quot;Empleados permitidos&quot;.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Mes</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  {MESES.map((nombre, i) => (
                    <option key={i} value={i}>{nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Año</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {modoImportacion === 'regularizar' ? 'Personal al que aplica' : 'Empleados que puede incluir en el tareo'} ({empleadosImportables.length})
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              {modoImportacion === 'regularizar'
                ? 'Solo puede regularizar conceptos para estos empleados del proyecto (el tareo debe existir en la fecha indicada).'
                : <>Proyecto <strong>{selectedProject?.nombre}</strong>, activos en {MESES[selectedMonth]} {selectedYear} según historial laboral.</>}
            </p>
            {empleadosImportables.length === 0 ? (
              <p className="text-sm text-amber-700">No hay empleados del proyecto activos en el mes/año seleccionado.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">DNI</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Nombre completo</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Cargo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {empleadosImportables.map((emp) => (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 text-gray-800">{emp.numero_documento || '—'}</td>
                        <td className="px-3 py-1.5 text-gray-800">{emp.nombre_completo || '—'}</td>
                        <td className="px-3 py-1.5 text-gray-600">{emp.cargo || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {modoImportacion === 'importar' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Archivo Excel</h3>
              <button type="button" onClick={descargarPlantilla} className="text-sm text-teal-600 hover:text-teal-800 font-medium">
                Descargar plantilla (con leyenda)
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Hoja &quot;Tareos&quot;: DNI, Fecha, Tipo evento, Horas teóricas/reales, Observaciones, Concepto (código o nombre), Monto concepto (opcional).
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
              {!archivo ? (
                <>
                  <p className="text-gray-600 mb-3">Seleccione un archivo .xlsx o .xls</p>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700">
                    Seleccionar archivo
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-700 font-medium">{archivo.name}</p>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm text-teal-600 hover:underline">Cambiar archivo</button>
                </>
              )}
            </div>
            {procesandoArchivo && (
              <div className="mt-3 flex items-center text-amber-700 text-sm">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent mr-2" />
                Procesando archivo...
              </div>
            )}
          </div>
          )}

          {modoImportacion === 'regularizar' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Archivo Excel - Regularizar conceptos</h3>
              <button type="button" onClick={descargarPlantillaRegularizar} className="text-sm text-teal-600 hover:text-teal-800 font-medium">
                Descargar plantilla (con leyenda y personal)
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Hoja &quot;Regularizar conceptos&quot;: DNI, Fecha, Concepto (código o nombre), Monto concepto, Observaciones. El tareo debe existir para ese empleado y fecha.
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input ref={fileInputRefRegularizar} type="file" accept=".xlsx,.xls" onChange={handleFileSelectRegularizar} className="hidden" />
              {!archivoRegularizar ? (
                <>
                  <p className="text-gray-600 mb-3">Seleccione un archivo .xlsx o .xls</p>
                  <button type="button" onClick={() => fileInputRefRegularizar.current?.click()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700">
                    Seleccionar archivo
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-700 font-medium">{archivoRegularizar.name}</p>
                  <button type="button" onClick={() => fileInputRefRegularizar.current?.click()} className="mt-2 text-sm text-teal-600 hover:underline">Cambiar archivo</button>
                </>
              )}
            </div>
            {procesandoRegularizar && (
              <div className="mt-3 flex items-center text-amber-700 text-sm">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent mr-2" />
                Procesando archivo...
              </div>
            )}
          </div>
          )}

          {modoImportacion === 'importar' && mostrarValidaciones && (
            <>
              <div className="mb-4 grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded p-3"><span className="text-lg font-bold text-gray-700">{datosExcel.length}</span><p className="text-xs text-gray-600">Total filas</p></div>
                <div className="bg-green-50 rounded p-3"><span className="text-lg font-bold text-green-700">{filasValidas.length}</span><p className="text-xs text-green-700">Válidas</p></div>
                <div className="bg-red-50 rounded p-3"><span className="text-lg font-bold text-red-700">{erroresValidacion.length}</span><p className="text-xs text-red-700">Con error</p></div>
              </div>

              {erroresValidacion.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg max-h-48 overflow-y-auto">
                  <h4 className="font-medium text-red-900 mb-2">Errores de validación (no se puede importar hasta corregirlos)</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {erroresValidacion.map((e, i) => (
                      <li key={i}>Fila {e.fila}: {e.nombre} — {e.mensaje}</li>
                    ))}
                  </ul>
                </div>
              )}

              {filasValidas.length > 0 && erroresValidacion.length === 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">Todas las filas son válidas según historial laboral. Puede proceder a importar.</p>
                </div>
              )}

              {filasValidas.length > 0 && (
                <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Vista previa de importación ({filasValidas.length} registros)</h4>
                  <p className="text-xs text-gray-500 mb-3">Revise los datos antes de importar. Así puede detectar errores antes de enviar.</p>
                  <div className="max-h-64 overflow-auto border border-gray-200 rounded">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">#</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">DNI</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Empleado</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Fecha</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Tipo</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Entrada T.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Salida T.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Entrada R.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Salida R.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Obs.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Conceptos</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filasValidas.map((fila, idx) => {
                          const emp = employees.find(e => e.id === fila.empleado_id);
                          const conceptoStr = fila.conceptos?.length
                            ? fila.conceptos.map(c => `${c.plame_configuracion_id || ''}: ${c.monto ?? ''}`).join('; ')
                            : '—';
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-2 py-1 text-gray-600">{fila.fila}</td>
                              <td className="px-2 py-1 text-gray-800">{emp?.numero_documento ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800 max-w-[120px] truncate" title={emp?.nombre_completo}>{emp?.nombre_completo ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800">{fila.fecha}</td>
                              <td className="px-2 py-1 text-gray-800">{fila.tipo_evento}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_entrada_teorica || '—'}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_salida_teorica || '—'}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_entrada_real || '—'}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_salida_real || '—'}</td>
                              <td className="px-2 py-1 text-gray-600 max-w-[80px] truncate" title={fila.observaciones || ''}>{fila.observaciones || '—'}</td>
                              <td className="px-2 py-1 text-gray-600 max-w-[100px] truncate" title={conceptoStr}>{conceptoStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {filasValidas.length > 0 && (
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={ejecutarImportacion}
                    disabled={importando || erroresValidacion.length > 0}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importando ? 'Importando...' : `Importar ${filasValidas.length} tareo(s)`}
                  </button>
                </div>
              )}
            </>
          )}

          {modoImportacion === 'regularizar' && mostrarValidacionesRegularizar && (
            <>
              <div className="mb-4 grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded p-3"><span className="text-lg font-bold text-gray-700">{datosRegularizar.length}</span><p className="text-xs text-gray-600">Total filas</p></div>
                <div className="bg-green-50 rounded p-3"><span className="text-lg font-bold text-green-700">{filasValidasRegularizar.length}</span><p className="text-xs text-green-700">Válidas</p></div>
                <div className="bg-red-50 rounded p-3"><span className="text-lg font-bold text-red-700">{erroresRegularizar.length}</span><p className="text-xs text-red-700">Con error</p></div>
              </div>

              {erroresRegularizar.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg max-h-48 overflow-y-auto">
                  <h4 className="font-medium text-red-900 mb-2">Errores de validación</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {erroresRegularizar.map((e, i) => (
                      <li key={i}>Fila {e.fila}: {e.nombre} — {e.mensaje}</li>
                    ))}
                  </ul>
                </div>
              )}

              {filasValidasRegularizar.length > 0 && erroresRegularizar.length === 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">Todas las filas son válidas. Los conceptos se añadirán a los tareos existentes.</p>
                </div>
              )}

              {filasValidasRegularizar.length > 0 && (
                <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Vista previa ({filasValidasRegularizar.length} registros)</h4>
                  <div className="max-h-64 overflow-auto border border-gray-200 rounded">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">#</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">DNI</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Empleado</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Fecha</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Concepto</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filasValidasRegularizar.map((fila, idx) => {
                          const emp = employees.find(e => e.id === fila.empleado_id);
                          const conceptoStr = fila.conceptos?.length
                            ? fila.conceptos.map((c) => {
                                const plame = (plameConceptos || []).find(p => String(p.id) === String(c.plame_configuracion_id));
                                const label = plame ? (plame.codigo_plame || plame.codigo || plame.nombre || c.tipo_concepto) : c.tipo_concepto;
                                return `${label || '—'}: ${c.monto ?? ''}`;
                              }).join('; ')
                            : '—';
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-2 py-1 text-gray-600">{idx + 1}</td>
                              <td className="px-2 py-1 text-gray-800">{emp?.numero_documento ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800 max-w-[120px] truncate" title={emp?.nombre_completo}>{emp?.nombre_completo ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800">{fila.fecha}</td>
                              <td className="px-2 py-1 text-gray-600 max-w-[120px] truncate" title={conceptoStr}>{conceptoStr}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.conceptos?.reduce((s, c) => s + (Number(c.monto) || 0), 0).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {filasValidasRegularizar.length > 0 && (
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={ejecutarRegularizacion}
                    disabled={aplicandoRegularizar || erroresRegularizar.length > 0}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aplicandoRegularizar ? 'Aplicando...' : 'Aplicar conceptos'}
                  </button>
                </div>
              )}
            </>
          )}

          {modoImportacion === 'importar' && resultado && (
            <div className={`mt-6 p-4 rounded-lg ${resultado.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {resultado.ok ? (
                <p className="text-green-800">{resultado.message}</p>
              ) : (
                <>
                  <p className="text-red-800 font-medium">{resultado.message}</p>
                  {hayErroresActividad && (
                    <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-red-900 mb-2">Empleados no activos en la fecha indicada (revise historial laboral):</p>
                      <ul className="text-sm text-red-800 space-y-1">
                        {resultado.errores_actividad.map((e, i) => (
                          <li key={i}>Fila {e.fila}: {e.empleado_nombre ?? e.empleado_id} — Fecha {e.fecha} — {e.mensaje}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hayErroresTipoEvento && (
                    <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-red-900 mb-2">Horas reales no permitidas para el tipo de evento (deben estar vacías para falta, feriado, descanso, permiso, vacaciones, etc.):</p>
                      <ul className="text-sm text-red-800 space-y-1">
                        {resultado.errores_tipo_evento.map((e, i) => (
                          <li key={i}>Fila {e.fila}: tipo {e.tipo_evento} — {e.mensaje}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {modoImportacion === 'regularizar' && resultadoRegularizar && (
            <div className={`mt-6 p-4 rounded-lg ${resultadoRegularizar.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {resultadoRegularizar.ok ? (
                <p className="text-green-800">{resultadoRegularizar.message}</p>
              ) : (
                <p className="text-red-800 font-medium">{resultadoRegularizar.message}</p>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportadorTareos;
